import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation, useRTL } from '../utils/translations';
import { useLogEvent, AnalyticsIds } from '../utils/useAnalytics';
import { getShareUrl } from '../utils/shareUrl';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  /** Where the modal was opened from: 'specDetails' | 'menu'. */
  source?: string;
}

const CopyIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="8" y="2" width="12" height="14" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const TwitterXIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const GmailIcon: React.FC = () => (
  <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="0.5" y="0.5" width="21" height="15" rx="1.5" stroke="white" strokeOpacity="0.6" />
    <path d="M1 1L11 9L21 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const WhatsAppIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
  </svg>
);

export const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, isDarkMode, source = '' }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const [copied, setCopied] = React.useState(false);
  const { trackEvent } = useLogEvent();

  React.useEffect(() => {
    if (isOpen) {
      trackEvent(AnalyticsIds.shareInteract, {
        state: true,
        parent: source,
        primaryTypeName: 'modal',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    trackEvent(AnalyticsIds.shareInteract, {
      state: false,
      parent: source,
      primaryTypeName: 'modal',
    });
    onClose();
  };

  const trackChannel = (channel: string) => {
    trackEvent(AnalyticsIds.shareInteract, {
      parent: source,
      primaryTypeName: channel,
    });
  };

  // Built per click (not at render): the modal stays mounted while the user keeps
  // configuring, and the link must carry the configuration as it is right now.
  const shareOptions = [
    {
      id: 'copy-link',
      label: copied
        ? t('staticTranslations.share.copied', 'Copied!')
        : t('staticTranslations.share.copyLink', 'Copy link'),
      bg: isDarkMode ? '#3a3a3a' : '#7f7f7f',
      iconColor: '#ffffff',
      action: () => {
        trackChannel('copyLink');
        navigator.clipboard.writeText(getShareUrl()).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      icon: (_color: string) => copied ? <CheckIcon /> : <CopyIcon />,
    },
    {
      id: 'facebook',
      label: t('staticTranslations.share.facebook', 'Facebook'),
      bg: '#306bfb',
      iconColor: '#fff',
      action: () => {
        trackChannel('facebook');
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`, '_blank', 'noopener');
      },
      icon: () => <FacebookIcon />,
    },
    {
      id: 'twitter',
      label: t('staticTranslations.share.twitter', 'Twitter'),
      bg: '#000000',
      iconColor: '#fff',
      action: () => {
        trackChannel('twitter');
        const text = t('staticTranslations.share.twitterTitle', 'Check out my car configuration!');
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(getShareUrl())}`, '_blank', 'noopener');
      },
      icon: () => <TwitterXIcon />,
    },
    {
      id: 'gmail',
      label: t('staticTranslations.share.gmail', 'Gmail'),
      bg: isDarkMode ? '#3a3a3a' : '#7f7f7f',
      iconColor: '#ffffff',
      action: () => {
        trackChannel('gmail');
        const subject = t('staticTranslations.share.emailSubject', 'Check out my car configuration');
        const body = t('staticTranslations.share.emailBody', 'I configured my dream car. Take a look:') + ' ' + getShareUrl();
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
      },
      icon: () => <GmailIcon />,
    },
    {
      id: 'whatsapp',
      label: t('staticTranslations.share.whatsapp', 'Whatsapp'),
      bg: '#25D366',
      iconColor: '#fff',
      action: () => {
        trackChannel('whatsapp');
        const text = t('staticTranslations.share.whatsappTitle', 'Check out my car configuration!') + ' ' + getShareUrl();
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      },
      icon: () => <WhatsAppIcon />,
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-[2px] z-[110]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            dir={isRTL ? 'rtl' : 'ltr'}
            className={cn(
              'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[111]',
              'w-[92vw] max-w-[420px] md:max-w-[579px]',
              'rounded-[var(--brand-dialog-radius)]',
              'shadow-2xl overflow-hidden',
              isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white',
            )}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              className={cn(
                'absolute rounded-full flex items-center justify-center transition-colors',
                'top-[14px] w-[24px] h-[24px] md:top-[20px] md:w-[29.67px] md:h-[29.67px]',
                isRTL ? 'left-[16px] md:left-[24px]' : 'right-[16px] md:right-[24px]',
                isDarkMode
                  ? 'border border-white/20 text-white/60 hover:border-white/40 hover:text-white'
                  : 'border border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-600',
              )}
            >
              <X className="w-[10px] h-[10px] md:w-[13px] md:h-[13px]" />
            </button>

            {/* Title */}
            <div className="pt-[28px] md:pt-[44px] pb-0 px-6 md:px-[52px]">
              <h2 className={cn(
                'text-center font-sans font-bold tracking-tight',
                'text-[20px] md:text-[32px]',
                isDarkMode ? 'text-white' : 'text-[#282828]',
              )}>
                {t('staticTranslations.share.saveAndShare', 'Save & Share')}
              </h2>
            </div>

            {/* Divider */}
            <div className={cn(
              'mt-[16px] md:mt-[29px] h-px w-full',
              isDarkMode ? 'bg-white/10' : 'bg-gray-200',
            )} />

            {/* Share options */}
            <div className="px-[20px] md:px-[52px] pt-[20px] md:pt-[34px] pb-[24px] md:pb-[55px]">
              <div className="flex items-start justify-between">
                {shareOptions.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={opt.action}
                    className="flex flex-col items-center gap-[8px] md:gap-[12px] group"
                  >
                    {/* Circle icon — 40px mobile / 57px desktop */}
                    <div
                      className="w-[40px] h-[40px] md:w-[57px] md:h-[57px] rounded-[39.9px] flex items-center justify-center transition-opacity group-hover:opacity-80 flex-shrink-0 overflow-hidden"
                      style={{ backgroundColor: opt.bg, color: opt.iconColor }}
                    >
                      <span className="scale-[0.7] md:scale-100 flex items-center justify-center">
                        {opt.icon(opt.iconColor)}
                      </span>
                    </div>
                    {/* Label — 10px mobile / 14px desktop */}
                    <span className={cn(
                      'text-center leading-tight font-sans font-medium whitespace-nowrap',
                      'text-[10px] md:text-[14px]',
                      isDarkMode ? 'text-white/70' : 'text-[#282828]',
                    )}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
